import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts"
import { Box, Typography, useTheme } from "@mui/material"
import type { ChatAnalyze } from "../interfaces"

interface WeeklyTrendChartProps {
  stats: ChatAnalyze.Stats
}

export function WeeklyTrendChart({ stats }: WeeklyTrendChartProps) {
  const theme = useTheme()

  const data = stats.weekStats

  if (!data.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Недостаточно данных для построения графика
      </Typography>
    )
  }

  // Форматирование метки недели: "2024-W03" → "W03"
  const formatWeek = (week: string) => {
    const parts = week.split("-")
    return parts.length === 2 ? parts[1] : week
  }

  return (
    <Box>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={data}
          margin={{ top: 4, right: 20, left: -10, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="week"
            tickFormatter={formatWeek}
            tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
            angle={-45}
            textAnchor="end"
            interval={Math.max(Math.floor(data.length / 20), 0)}
          />
          <YAxis
            yAxisId="msgs"
            orientation="left"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            tickFormatter={(v) => v.toLocaleString("ru-RU")}
          />
          <YAxis
            yAxisId="users"
            orientation="right"
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
          />
          <Tooltip
            labelFormatter={(label) => `Неделя: ${label}`}
            formatter={(
              value: number | undefined,
              name: string | undefined,
            ) => [
              (value ?? 0).toLocaleString("ru-RU"),
              name === "messages" ? "Сообщений" : "Уник. авторов",
            ]}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 4,
              fontSize: 12,
            }}
          />
          <Legend
            formatter={(value) =>
              value === "messages" ? "Сообщений" : "Уник. авторов"
            }
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar
            yAxisId="msgs"
            dataKey="messages"
            fill={theme.palette.info.main}
            opacity={0.7}
            radius={[2, 2, 0, 0]}
          />
          <Line
            yAxisId="users"
            dataKey="users"
            stroke={theme.palette.secondary.light}
            strokeWidth={2}
            dot={false}
            type="monotone"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
